import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks";
import { CompanyGroup } from "types/CompanyGroup";
import { fetchCompanies, fetchCompanyGroups } from "./company-select.slice";

/**
 * A group visitor is a person with group rights who opens a company of the
 * group where they are NOT a member. In that company they see only the news
 * feed and the "About" page, and the app must not call anything else for it
 * (the role request would answer 403, which logs the person out).
 *
 * - "regular": the person is a member of the company (or it is no group company
 *   at all) - the app works exactly as it did before groups.
 * - "visitor": a group company of the person where isMember is false.
 * - "pending": member companies or groups are still loading; the answer is not
 *   known yet, so nothing company-specific may be requested.
 * - "forbidden": both lists have loaded and the company is in neither (for
 *   example the person's membership there was switched off): the app sends the
 *   person back to the company list instead of asking the company anything.
 *   See useRecheckForbiddenCompany: the lists may be old.
 */
export type CompanyAccessMode = "pending" | "regular" | "visitor" | "forbidden";

interface AccessModeInput {
  companyId: string | undefined;
  memberCompanies: { id: string }[];
  haveMemberCompaniesLoaded: boolean;
  // A failed load leaves the list empty, which says nothing about this company.
  didMemberCompaniesFail: boolean;
  groups: CompanyGroup[];
  haveGroupsLoaded: boolean;
  didGroupsFail: boolean;
}

export function getCompanyAccessMode({
  companyId,
  memberCompanies,
  haveMemberCompaniesLoaded,
  didMemberCompaniesFail,
  groups,
  haveGroupsLoaded,
  didGroupsFail,
}: AccessModeInput): CompanyAccessMode {
  if (!companyId) return "regular";
  if (!haveMemberCompaniesLoaded) return "pending";
  // A member company needs no group answer, so members do not wait for it.
  if (memberCompanies.some((company) => company.id === companyId)) {
    return "regular";
  }
  if (!haveGroupsLoaded) return "pending";

  const isVisitedGroupCompany = groups.some((group) =>
    group.companies.some(
      (company) => company.id === companyId && !company.isMember
    )
  );
  if (isVisitedGroupCompany) return "visitor";

  // Let the server decide when a list is unknown.
  return didMemberCompaniesFail || didGroupsFail ? "regular" : "forbidden";
}

export function useCompanyAccessMode(
  companyId: string | undefined
): CompanyAccessMode {
  // The slice is plain JS, so its state is not typed.
  const companySelect = useSelector((state: any) => state.companySelectReducer);
  return getCompanyAccessMode({
    companyId,
    memberCompanies: companySelect.companiesCollectionRaw,
    haveMemberCompaniesLoaded: companySelect.hasLoaded,
    didMemberCompaniesFail: companySelect.isError,
    groups: companySelect.companyGroups,
    haveGroupsLoaded: companySelect.hasLoadedGroups,
    didGroupsFail: companySelect.didGroupsFail,
  });
}

/**
 * Both lists are loaded once per session, so a membership added or switched on since
 * then is missing from them. Before a company counts as forbidden, load both lists
 * again, once per company. Returns true while that runs: show the loading state.
 */
export function useRecheckForbiddenCompany(
  companyId: string | undefined,
  accessMode: CompanyAccessMode
): boolean {
  const dispatch = useAppDispatch();
  const [recheckedCompanyId, setRecheckedCompanyId] = useState<string | undefined>();
  const isRechecking = accessMode === "forbidden" && recheckedCompanyId !== companyId;

  useEffect(() => {
    if (!isRechecking) return;
    Promise.all([dispatch(fetchCompanies()), dispatch(fetchCompanyGroups())]).then(() =>
      setRecheckedCompanyId(companyId)
    );
  }, [dispatch, isRechecking, companyId]);

  return isRechecking;
}

/** The only pages a visitor may open in a company, as the part after /app/<companyId>/. */
const VISITOR_PAGES = ["posts", "about-company"];

export function isVisitorPage(pathname: string, companyId: string): boolean {
  const companyPrefix = `/app/${companyId}/`;
  if (!pathname.startsWith(companyPrefix)) return false;
  const page = pathname.slice(companyPrefix.length).replace(/\/$/, "");
  return VISITOR_PAGES.includes(page);
}
